import { useMutation, useQuery } from 'react-query';
import { User } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { IAccountData } from '../../interfaces/account-data.interface';
import { baseApi, buildAuthHeaders, throwApiError, useRetrieveToken } from './shared';

export const useAccountApi = () => {
  const { retrieveToken, isAuthenticated } = useRetrieveToken();

  const useAccount = () => {
    return useQuery<unknown, unknown, IAccountData, any>(['account'], async ({ signal }) => {
      const token = await retrieveToken('getAccount');
      if (!token) return;

      const response = await fetch(`${baseApi}/account/`, {
        method: 'GET',
        headers: buildAuthHeaders(token),
        signal,
      });

      if (!response.ok) {
        datadogLogs.logger.error('Failed to fetch user data', {
          name: 'getAccount',
          response: { status: response.status, statusText: response.statusText },
        });
        throw new Error('Failed to fetch user data');
      }

      return response.json();
    });
  };

  const updateAccount = useMutation(async (userData: User) => {
    const token = await retrieveToken('updateAccount');
    if (!token) return;

    const response = await fetch(`${baseApi}/account/${userData.sub}`, {
      method: 'PUT',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to update user data', {
        name: 'updateAccount',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to update user data');
    }

    return response.json();
  });

  const useUsage = ({
    start,
    limit,
    sort,
    sortDirection,
  }: {
    start: number;
    limit: number;
    sort: string;
    sortDirection: 'asc' | 'desc';
  }) => {
    return useQuery<unknown, unknown, any, any>(
      ['usage', start, limit, sort, sortDirection],
      async ({ signal }) => {
        const token = await retrieveToken('getUsage');
        if (!token) return;

        const response = await fetch(
          `${baseApi}/usage?start=${start}&limit=${limit}&sort=${sort}&sortDirection=${sortDirection}`,
          {
            method: 'GET',
            headers: buildAuthHeaders(token),
            signal,
          },
        );

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch usage data', { name: 'getUsage' });
          throw new Error('Failed to fetch usage data');
        }

        return response.json();
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, cacheTime: 3600000, enabled: isAuthenticated },
    );
  };

  const getResellers = useQuery(
    'resellers',
    async () => {
      const token = await retrieveToken('getResellers');
      if (!token) return [];

      const response = await fetch(`${baseApi}/account/resellers`, {
        method: 'GET',
        headers: buildAuthHeaders(token),
      });

      if (!response.ok) await throwApiError(response, 'getResellers');

      return response.json();
    },
    { enabled: isAuthenticated },
  );

  const getResellersWithCredit = useQuery(
    ['resellers-with-credit'],
    async () => {
      const token = await retrieveToken('getResellersWithCredit');
      if (!token) return [];

      const response = await fetch(`${baseApi}/credit`, {
        method: 'GET',
        headers: buildAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch resellers with credit');
      }

      return response.json();
    },
    { enabled: isAuthenticated, refetchOnWindowFocus: false },
  );

  return {
    useAccount,
    updateAccount,
    useUsage,
    getResellers,
    getResellersWithCredit,
  };
};
